import React, { useEffect, useState } from "react";
import config from "lib/fr-config";
import Draggable from "react-draggable";
import ReactResizeDetector from "react-resize-detector";
import { getDeviceType } from "lib/deviceInfo";
import CommonSplitView from "presentational/common/CommonSplitView";
import BrowserResizeDetector from "presentational/common/BrowserResizeDetector";
import OutfitImageView from "presentational/common/OutfitImageView";
import IconButtonView from "presentational/common/IconButtonView";
import Tooltip from "@material-ui/core/Tooltip";
import { withStyles } from "@material-ui/core/styles";
import PropTypes from "prop-types";

const IMAGE_RATIO = config.outfitImage.RATIO;

/** TOP_AND_BOTTOM_MARGIN - 홈버튼없는 아이폰의 하단 홈인디케이터 높이도 고려해 설정. */
const TOP_AND_BOTTOM_MARGIN = 20;

const desktopCss = config.css.desktop;
const mobileCss = config.css.mobile;
const colorCss = config.css.color;

const WIDTH_OF_ESSENTIAL_AREA_FOR_MOBILE = mobileCss.sidePadding * 2;
const HEIGHT_OF_ESSENTIAL_AREA_FOR_MOBILE =
  mobileCss.logoBarHeightPX +
  mobileCss.headerBarHeightPX +
  TOP_AND_BOTTOM_MARGIN;

const CustomTooltip = withStyles({
  tooltip: {
    // borderRadius: 0,
    backgroundColor: colorCss.colorOfTooltipPrimary
  },
  arrow: {
    color: colorCss.colorOfTooltipPrimary
  },
  popper: { zIndex: 90 }
})(Tooltip);

StickerAdderPanelView.propTypes = {
  imageDataURL: PropTypes.string.isRequired,
  sticker: PropTypes.string.isRequired,
  onResize: PropTypes.func.isRequired,
  tooltipOpen: PropTypes.bool.isRequired,
  tooltipText: PropTypes.oneOfType([PropTypes.string, PropTypes.object])
    .isRequired,
  imageWidth: PropTypes.number.isRequired,
  imageHeight: PropTypes.number.isRequired,
  stickerWidth: PropTypes.number.isRequired,
  stickerHeight: PropTypes.number.isRequired,
  visible: PropTypes.bool.isRequired,
  percentX: PropTypes.number.isRequired,
  percentY: PropTypes.number.isRequired,
  onDragStart: PropTypes.func.isRequired,
  onDragStop: PropTypes.func.isRequired,
  onToggleClick: PropTypes.func.isRequired,
  onBackClick: PropTypes.func.isRequired,
  onNextClick: PropTypes.func.isRequired
};

function StickerAdderPanelView(props) {
  const {
    imageDataURL,
    sticker,
    onResize,
    tooltipOpen,
    tooltipText,
    imageWidth,
    imageHeight,
    stickerWidth,
    stickerHeight,
    visible,
    percentX,
    percentY,
    onDragStart,
    onDragStop,
    onToggleClick,
    onBackClick,
    onNextClick
  } = props;
  const [typeOfScreenSize, setTypeOfScreenSize] = useState("pending");
  const [viewportHeight, setViewportHeight] = useState(0);
  const [topPaddingOfRootForMobile, setTopPaddingOfRootForMobile] = useState(0);

  useEffect(() => {
    handleResizeForMobile();
  });

  const getCssXL = () => ({
    imageWrapper: {
      width: "100%",
      position: "relative"
    },
    buttonArea: {
      display: "flex",
      justifyContent: "flex-end",
      marginBottom: desktopCss.standardPaddingBetweenContent
    },
    sticker: {
      position: "absolute",
      top: 0,
      left: 0
    },
    glass: {
      position: "absolute",
      top: 0,
      left: 0,
      bottom: 0,
      right: 0
    }
  });
  const getCssXS = () => {
    const widthOfImgWrapper =
      typeOfScreenSize === "short"
        ? `calc((${viewportHeight}px - ${HEIGHT_OF_ESSENTIAL_AREA_FOR_MOBILE}px) * ${IMAGE_RATIO})`
        : undefined;
    return {
      root: {
        marginTop: -mobileCss.paddingBetweenHeaderbarAndFirstContent,
        paddingTop: topPaddingOfRootForMobile
      },
      imageArea: {
        display: "flex",
        justifyContent: "center"
      },
      imageWrapper: {
        position: "relative",
        margin: "auto",
        width: widthOfImgWrapper || "100%"
      },
      buttonArea: {
        display: "flex",
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1100,
        backgroundColor: "white",
        alignItems: "center",
        justifyContent: "space-around",

        height: `calc(env(safe-area-inset-bottom) + ${mobileCss.headerBarHeightPX}px)`
      },
      buttonWrapper: {
        paddingBottom: "calc(env(safe-area-inset-bottom))"
      },
      sticker: {
        position: "absolute",
        top: 0,
        left: 0
      },
      glass: {
        position: "absolute",
        top: 0,
        left: 0,
        bottom: 0,
        right: 0
      }
    };
  };

  const handleResizeForMobile = () => {
    if (getDeviceType() === "desktop") return null;

    const { innerWidth: windowWidth, innerHeight: newViewportHeight } = window;
    const maxWidthOfImage = windowWidth - 2 * mobileCss.sidePadding;
    const heightOfAllContent =
      maxWidthOfImage / IMAGE_RATIO + HEIGHT_OF_ESSENTIAL_AREA_FOR_MOBILE;
    const screenType =
      newViewportHeight > heightOfAllContent ? "long" : "short";
    const widthOfPlacingArea = windowWidth - WIDTH_OF_ESSENTIAL_AREA_FOR_MOBILE;
    const heightOfPlacingArea =
      newViewportHeight - HEIGHT_OF_ESSENTIAL_AREA_FOR_MOBILE;
    const maxHeightOfImage = widthOfPlacingArea / IMAGE_RATIO;
    const heightOfVoidArea = heightOfPlacingArea - maxHeightOfImage;
    const topPaddingOfRoot = heightOfVoidArea <= 0 ? 0 : heightOfVoidArea / 2;

    if (
      typeOfScreenSize !== screenType ||
      viewportHeight !== newViewportHeight ||
      topPaddingOfRoot !== topPaddingOfRootForMobile
    ) {
      setTypeOfScreenSize(screenType);
      setViewportHeight(newViewportHeight);
      setTopPaddingOfRootForMobile(topPaddingOfRoot);
    }
  };

  const getWidthOfLeftSide = fixedHeightValue => {
    const { innerHeight } = window;
    const imageHeight = innerHeight - fixedHeightValue;
    return imageHeight * IMAGE_RATIO;
  };

  const getReadyToDisplaySticker = () => {
    return (
      visible &&
      imageWidth !== 0 &&
      imageHeight !== 0 &&
      stickerWidth !== 0 &&
      stickerHeight !== 0
    );
  };

  const mobileLayout = () => {
    const css = getCssXS();

    return (
      <div style={css.root}>
        <BrowserResizeDetector onResize={handleResizeForMobile} />
        <div style={css.imageArea}>
          <div style={css.imageWrapper}>
            <OutfitImageView imageSrc={imageDataURL} />
            <ReactResizeDetector handleWidth handleHeight onResize={onResize} />
            {getReadyToDisplaySticker() && (
              <CustomTooltip
                open={tooltipOpen}
                title={tooltipText}
                placement="bottom"
                disableFocusListener
                disableHoverListener
                disableTouchListener
                arrow
              >
                <Draggable
                  bounds={{
                    left: 0,
                    top: 0,
                    right: imageWidth - stickerWidth,
                    bottom: imageHeight - stickerHeight
                  }}
                  position={{
                    x: Math.ceil(percentX * (imageWidth - stickerWidth)),
                    y: Math.ceil(percentY * (imageHeight - stickerHeight))
                  }}
                  onStart={onDragStart}
                  onStop={onDragStop}
                  onMouseDown={e => {
                    e.preventDefault();
                  }}
                >
                  <div style={css.sticker}>
                    <img
                      alt=""
                      src={sticker}
                      style={{
                        width: stickerWidth,
                        height: stickerHeight
                      }}
                    />
                    <div style={css.glass} />
                  </div>
                </Draggable>
              </CustomTooltip>
            )}
          </div>
        </div>
        <div style={css.buttonArea}>
          <div style={css.buttonWrapper}>
            <IconButtonView type={"back"} onClick={onBackClick} />
          </div>
          <div style={css.buttonWrapper}>
            <IconButtonView type={"addSticker"} onClick={onToggleClick} />
          </div>
          <div style={css.buttonWrapper}>
            <IconButtonView type={"next"} onClick={onNextClick} />
          </div>
        </div>
      </div>
    );
  };

  const desktopLayout = () => {
    const css = getCssXL();

    return (
      <div>
        <CommonSplitView
          getWidthOfLeftSide={getWidthOfLeftSide}
          leftSideComponent={
            <div style={css.imageWrapper}>
              <OutfitImageView imageSrc={imageDataURL} />
              <ReactResizeDetector
                handleWidth
                handleHeight
                onResize={onResize}
              />
              {getReadyToDisplaySticker() && (
                <CustomTooltip
                  open={tooltipOpen}
                  title={tooltipText}
                  placement="bottom"
                  disableFocusListener
                  disableHoverListener
                  disableTouchListener
                  arrow
                >
                  <Draggable
                    bounds={{
                      left: 0,
                      top: 0,
                      right: imageWidth - stickerWidth,
                      bottom: imageHeight - stickerHeight
                    }}
                    position={{
                      x: Math.ceil(percentX * (imageWidth - stickerWidth)),
                      y: Math.ceil(percentY * (imageHeight - stickerHeight))
                    }}
                    onStart={onDragStart}
                    onStop={onDragStop}
                    onMouseDown={e => {
                      e.preventDefault();
                    }}
                  >
                    <div style={css.sticker}>
                      <img
                        alt=""
                        src={sticker}
                        style={{
                          width: stickerWidth,
                          height: stickerHeight
                        }}
                      />
                      <div style={css.glass} />
                    </div>
                  </Draggable>
                </CustomTooltip>
              )}
            </div>
          }
          rightSideComponent={
            <div style={css.buttonArea}>
              <IconButtonView type={"back"} onClick={onBackClick} />
              <IconButtonView type={"addSticker"} onClick={onToggleClick} />
              <IconButtonView
                type={"next"}
                disableRightMargin
                onClick={onNextClick}
              />
            </div>
          }
        />
      </div>
    );
  };

  const render = () => {
    return getDeviceType() === "mobile" ? mobileLayout() : desktopLayout();
  };

  return render();
}

export default StickerAdderPanelView;
